import app from 'flarum/forum/app';
import Notification from 'flarum/forum/components/Notification';
import { extend } from 'flarum/common/extend';

/**
 * Voucha's own notifications (Notify\Activity on the forum): a joined topic's discussion passing 10 comments, policy
 * notices, a topic's milestones for its moderators, a business's busy week for its owners, the weekly email.
 */
const content = n => n.content() || {};
const rise = (now, before) => before ? now + ' (' + before + ' the week before)' : String(now);
class PopularNotification extends Notification {
  icon() {
    return 'fas fa-fire';
  }
  href() {
    const d = this.attrs.notification.subject();
    return d ? app.route.discussion(d) : '/';
  }
  content() {
    const c = content(this.attrs.notification);
    return 'Busy in ' + (c.tag || 'a community you joined') + ': ' + (c.comments || 'many') + ' comments';
  }
  excerpt() {
    const d = this.attrs.notification.subject();
    return d && d.title ? d.title() : '';
  }
}
class PolicyNotification extends Notification {
  icon() {
    return 'fas fa-scale-balanced';
  }
  href() {
    const d = this.attrs.notification.subject();
    return d ? app.route.discussion(d) : '/';
  }
  content() {
    return 'Policy update';
  }
  excerpt() {
    const d = this.attrs.notification.subject();
    return d && d.title ? d.title() : '';
  }
}
class TopicMilestoneNotification extends Notification {
  icon() {
    return content(this.attrs.notification).kind === 'members' ? 'fas fa-user-plus' : 'fas fa-chart-line';
  }
  href() {
    return '/t/' + (content(this.attrs.notification).slug || '');
  }
  content() {
    const c = content(this.attrs.notification);
    return c.kind === 'members' ? c.added + ' new members joined ' + c.name : c.name + ' is busier than last week';
  }
  excerpt() {
    const c = content(this.attrs.notification);
    if (c.kind === 'members') return c.members + ' members now · a community you moderate';
    return [c.visitors ? 'Visitors: ' + rise(c.visitors, c.visitorsBefore) : null, c.posts ? 'Posts: ' + rise(c.posts, c.postsBefore) : null].filter(Boolean).join(' · ');
  }
}
class BusinessActivityNotification extends Notification {
  icon() {
    return 'fas fa-store';
  }
  href() {
    return '/businesses/' + (content(this.attrs.notification).slug || '');
  }
  content() {
    return (content(this.attrs.notification).name || 'Your business') + ' is getting more attention';
  }
  excerpt() {
    const c = content(this.attrs.notification);
    return [c.views ? 'Page visits: ' + rise(c.views, c.viewsBefore) : null, c.vouches ? c.vouches + ' new vouches' : null].filter(Boolean).join(' · ');
  }
}
class ProjectProgressNotification extends Notification {
  icon() {
    return content(this.attrs.notification).stage === 'done' ? 'fas fa-flag-checkered' : 'fas fa-people-carry-box';
  }
  href() {
    return '/projects/' + (content(this.attrs.notification).slug || '');
  }
  content() {
    const c = content(this.attrs.notification);
    return (c.name || 'A project you follow') + (c.stage === 'done' ? ' is completed' : ' is halfway done');
  }
  excerpt() {
    const c = content(this.attrs.notification);
    return c.state ? 'Community project in ' + c.state : '';
  }
}
const REMINDER_ICON = {
  milestone_soon: 'far fa-clock',
  milestone_overdue: 'fas fa-triangle-exclamation',
  funding_near: 'fas fa-hourglass-half',
  funded: 'fas fa-sack-dollar',
  savings: 'fas fa-piggy-bank',
  stalled: 'fas fa-circle-pause'
};
const cash = (v, c) => ({
  NGN: '₦',
  USD: '$',
  GBP: '£',
  EUR: '€',
  CAD: 'C$'
}[c || 'NGN'] || '') + Number(v || 0).toLocaleString('en-US', {
  maximumFractionDigits: 0
});
const day = d => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC'
}) : '';

/** Reminders about a project the member runs (Projects::runReminders) */
class ProjectReminderNotification extends Notification {
  icon() {
    return REMINDER_ICON[content(this.attrs.notification).kind] || 'far fa-bell';
  }
  href() {
    return '/projects/' + (content(this.attrs.notification).slug || '');
  }
  content() {
    const c = content(this.attrs.notification);
    const n = c.name || 'Your project';
    switch (c.kind) {
      case 'milestone_soon':
        return n + ': “' + c.milestone + '” is due ' + day(c.due);
      case 'milestone_overdue':
        return n + ': “' + c.milestone + '” is overdue';
      case 'funding_near':
        return n + ': the money is needed by ' + day(c.by);
      case 'funded':
        return n + ' has reached its funding target';
      case 'savings':
        return n + ': time to check your savings';
      case 'stalled':
        return n + ' hasn’t had an update in three months';
      default:
        return n;
    }
  }
  excerpt() {
    const c = content(this.attrs.notification);
    if (c.kind === 'funding_near') return cash(c.remaining, c.currency) + ' still to find';
    if (c.kind === 'savings') return cash(c.saved, c.currency) + ' of ' + cash(c.target, c.currency) + ' so far';
    if (c.kind === 'stalled') return 'Post an update, or mark it paused, completed or cancelled';
    return '';
  }
}
class DigestNotification extends Notification {
  icon() {
    return 'far fa-envelope';
  }
  href() {
    const c = content(this.attrs.notification);
    const b = (c.businesses || [])[0];
    const t = (c.topics || [])[0];
    return t ? '/t/' + t.slug : b ? '/businesses/' + b.slug : '/';
  }
  content() {
    return 'Your week on Voucha';
  }
  excerpt() {
    const c = content(this.attrs.notification);
    return (c.topics || []).map(t => t.name).concat((c.businesses || []).map(b => b.name)).join(', ');
  }
}
export default function registerNotifications() {
  app.notificationComponents.vouchaPopular = PopularNotification;
  app.notificationComponents.vouchaPolicy = PolicyNotification;
  app.notificationComponents.vouchaTopicMilestone = TopicMilestoneNotification;
  app.notificationComponents.vouchaBusinessActivity = BusinessActivityNotification;
  app.notificationComponents.vouchaWeeklyDigest = DigestNotification;
  app.notificationComponents.vouchaProjectProgress = ProjectProgressNotification;
  app.notificationComponents.vouchaProjectReminder = ProjectReminderNotification;

  // Settings > Notifications: a row each, with the bell and email switches
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    items.add('vouchaPopular', {
      name: 'vouchaPopular',
      icon: 'fas fa-fire',
      label: 'A discussion in a community I joined passes 10 comments'
    });
    items.add('vouchaPolicy', {
      name: 'vouchaPolicy',
      icon: 'fas fa-scale-balanced',
      label: 'A new policy update in the Policy community (if I joined it)'
    });
    items.add('vouchaTopicMilestone', {
      name: 'vouchaTopicMilestone',
      icon: 'fas fa-user-plus',
      label: 'A community I moderate gets 10 new members or a busy week'
    });
    items.add('vouchaBusinessActivity', {
      name: 'vouchaBusinessActivity',
      icon: 'fas fa-store',
      label: 'My business page gets a lot more visits or vouches'
    });
    items.add('vouchaProjectProgress', {
      name: 'vouchaProjectProgress',
      icon: 'fas fa-people-carry-box',
      label: 'A project I follow or run is halfway done or completed'
    });
    items.add('vouchaProjectReminder', {
      name: 'vouchaProjectReminder',
      icon: 'far fa-bell',
      label: 'Reminders for my projects: milestones due, funding dates and targets, savings checks, no updates for a while'
    });
    items.add('vouchaWeeklyDigest', {
      name: 'vouchaWeeklyDigest',
      icon: 'far fa-envelope',
      label: 'Weekly summary of the communities I moderate and my businesses'
    });
  });
}